// /admin/agents — fleet view of autonomous/scripted scan submitters (T1015).
//
// Backend: GET /api/v1/admin/agents(?window_days=N). org_admin role
// required; every license tier (same "no license.Feature gate" precedent as
// /admin/usage — see admin_agents.go's own doc comment).
//
// Every ratio ships with its own sample size instead of a bare percentage
// (T1014's own framing: "an agent with three scans doesn't have a rate, it
// has noise"). This page renders that honestly — a null `value` (zero
// denominator) always reads "not enough data", never a smoothed-over 0%,
// the same principle T913 established for the project quality-profile
// panel. Sample size is shown next to every metric regardless of whether
// it's computable.

import { ReactNode, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ArrowDown, ArrowUp, ArrowUpDown, Bot, Loader2 } from 'lucide-react';
import { AgentMetric, AgentSummary, ApiError, adminAgentsApi } from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

const WINDOWS: { label: string; days: number }[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

type SortKey = 'findings_per_kloc' | 'fix_rate' | 'suppression_rate' | 'reintroduction_rate';

const COLUMNS: { key: SortKey; label: string; hint: string }[] = [
  { key: 'findings_per_kloc', label: 'Findings / KLOC', hint: 'Introduced findings per 1000 lines scanned, this window.' },
  { key: 'fix_rate', label: 'Fix rate', hint: 'Resolved ÷ introduced, from this agent’s own scans.' },
  { key: 'suppression_rate', label: 'Suppression rate', hint: 'Ignore-rule/profile/resolution suppression signals ÷ resolved. Not inherently bad — see hint.' },
  { key: 'reintroduction_rate', label: 'Reintroduction rate', hint: 'Introduced findings whose fingerprint another agent had already resolved, ÷ introduced.' },
];

function agentsKey(windowDays: number): string {
  return `/api/v1/admin/agents?window_days=${windowDays}`;
}

export function AdminAgents() {
  const [windowDays, setWindowDays] = useState(7);
  const [sortKey, setSortKey] = useState<SortKey>('findings_per_kloc');
  const [sortDesc, setSortDesc] = useState(true);

  const { data, error, isLoading } = useSWR<AgentSummary[]>(
    agentsKey(windowDays),
    () => adminAgentsApi.list({ windowDays }),
    { revalidateOnFocus: false },
  );

  const sorted = useMemo(() => {
    if (!data) return [];
    const withRank = data.map((a) => ({ agent: a, v: a[sortKey].value }));
    withRank.sort((x, y) => {
      // Agents with "not enough data" (null value) always sort last,
      // regardless of direction — an incomparable quote is not "low".
      if (x.v === null && y.v === null) return 0;
      if (x.v === null) return 1;
      if (y.v === null) return -1;
      return sortDesc ? y.v - x.v : x.v - y.v;
    });
    return withRank.map((r) => r.agent);
  }, [data, sortKey, sortDesc]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDesc((d) => !d);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading agent activity…</span>
      </div>
    );
  } else if (error) {
    body = <ErrorBanner error={error} />;
  } else if (!data || data.length === 0) {
    body = (
      <div className="orb-card p-8 text-center text-fg-muted text-sm">
        No agent scan activity in this window.
      </div>
    );
  } else {
    body = (
      <AgentTable agents={sorted} sortKey={sortKey} sortDesc={sortDesc} onSort={handleSort} />
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Bot />}
        title="Agents"
        description={
          <>
            Every scan submitter (user or project token) with activity in the
            window, ranked by the four fleet metrics. Every ratio is a{' '}
            <em>ratio</em>, not a verdict — an agent with a handful of scans
            doesn’t have a rate, it has noise, and this page says so plainly
            instead of printing a percentage that looks more certain than it
            is.
          </>
        }
      />

      <div className="orb-card p-1 mb-6 inline-flex gap-1" role="tablist" aria-label="Reporting window">
        {WINDOWS.map((w) => (
          <button
            key={w.days}
            type="button"
            role="tab"
            aria-selected={windowDays === w.days}
            onClick={() => setWindowDays(w.days)}
            className={`
              px-3 py-1.5 rounded text-xs font-semibold transition-colors
              ${windowDays === w.days ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'}
            `}
          >
            {w.label}
          </button>
        ))}
      </div>

      {body}
    </div>
  );
}

// formatMetricValue renders findings_per_kloc (a rate-per-1000-lines, not a
// fraction) as a plain number; the other three metrics are fractions in
// [0,1], rendered as a percentage. Which one applies is the caller's
// `kind`, not something derivable from the value itself.
function formatMetricValue(v: number, kind: 'kloc' | 'rate'): string {
  return kind === 'rate' ? `${(v * 100).toFixed(1)}%` : v.toFixed(2);
}

interface AgentTableProps {
  readonly agents: AgentSummary[];
  readonly sortKey: SortKey;
  readonly sortDesc: boolean;
  readonly onSort: (key: SortKey) => void;
}

function AgentTable({ agents, sortKey, sortDesc, onSort }: AgentTableProps) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>Agent</TH>
          <TH align="right">Scans</TH>
          {COLUMNS.map((col) => (
            <TH key={col.key} align="right" title={col.hint}>
              <button
                type="button"
                onClick={() => onSort(col.key)}
                className="inline-flex items-center gap-1 hover:text-fg transition-colors"
              >
                {col.label}
                <SortIcon active={sortKey === col.key} desc={sortDesc} />
              </button>
            </TH>
          ))}
        </TR>
      </THead>
      <TBody>
        {agents.map((a) => (
          <TR key={`${a.actor_kind}:${a.actor_id}`}>
            <TD primary>
              <p className="font-mono text-xs text-fg">{a.actor_id}</p>
              <p className="text-[11px] text-fg-muted">{a.actor_kind}</p>
            </TD>
            <TD align="right" mono primary>{a.scan_count}</TD>
            <TD>
              <MetricCell metric={a.findings_per_kloc} kind="kloc" />
            </TD>
            <TD>
              <MetricCell metric={a.fix_rate} kind="rate" />
            </TD>
            <TD>
              <MetricCell metric={a.suppression_rate} kind="rate" />
            </TD>
            <TD>
              <MetricCell metric={a.reintroduction_rate} kind="rate" />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function SortIcon({ active, desc }: { readonly active: boolean; readonly desc: boolean }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />;
  return desc ? (
    <ArrowDown className="h-3 w-3" aria-hidden="true" />
  ) : (
    <ArrowUp className="h-3 w-3" aria-hidden="true" />
  );
}

function MetricCell({ metric, kind }: { readonly metric: AgentMetric; readonly kind: 'kloc' | 'rate' }) {
  const display = metric.value === null ? 'not enough data' : formatMetricValue(metric.value, kind);
  return (
    <div className="text-right">
      <p className={`text-sm font-mono ${metric.value === null ? 'text-fg-muted italic' : 'text-fg'}`}>
        {display}
      </p>
      <p className="text-[11px] text-fg-muted">n={metric.sample_size}</p>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view fleet telemetry.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
